import { FC, useEffect, useMemo } from "react";
import useNavigateTo from "hooks/useNavigateTo";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppSelector } from "hooks/useStore";
// Selectors
import { selectQuiz } from "store/quiz/quizSelectors";
import { selectMaxScore, selectPreviewPageMode, selectTotalScore } from "store/player/playerSelectors";
// Components
import AppBar from "components/AppBar";
import { Button } from "components/Buttons";
import PoweredBy from "components/PoweredBy";
// Icons
import { GiTrophyCup } from "react-icons/gi";

const ScoringResultPage:FC = () => {
  const { t } = useTranslation();

  const navigateTo = useNavigateTo();

  const quiz = useAppSelector(selectQuiz);
  const totalScore = useAppSelector(selectTotalScore);
  const maxScore = useAppSelector(selectMaxScore);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

  const onClick = () => navigateTo('pages-handler');

  useEffect(() => {
    if (!quiz.scoringResultPage?.enabled && !previewPageMode) onClick();
    // eslint-disable-next-line
  }, []);

  const title = useMemo(() => {
    const percentage = (totalScore / maxScore) * 100;
    if (percentage >= 76) return t('pages.scoringResult.outstandingLabel');
    if (percentage >= 51) return t('pages.scoringResult.wellDoneLabel');
    if (percentage >= 26) return t('pages.scoringResult.niceEffortLabel');
    return t('pages.scoringResult.goodTryLabel');
  }, [totalScore, maxScore, t]);

  const { primaryColor } = quiz.scoringResultPage || {};

  if (!quiz.scoringResultPage?.enabled && !previewPageMode) return null;
  return (
    <div
      className="p-4 flex flex-col w-full h-full bg-gray-200 overflow-y-auto"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') center / cover` : '' }}
    >
      <AppBar />
      <div className="flex-1 text-primary pt-8">
        <GiTrophyCup className="text-7xl mx-auto mb-4" style={{ color: primaryColor }} />
        <h1 className="text-center text-3xl font-bold uppercase mb-2" style={{ color: primaryColor }}>{title}</h1>
        {quiz.scoringResultPage.text ? (
          <p className="text-center text-md mb-8">{quiz.scoringResultPage.text}</p>
        ) : null}

        {quiz.scoringResultPage.image ? (
          <img src={quiz.scoringResultPage.image} alt="" className="w-[75%] mx-auto mb-8 shadow-primary" />
        ) : null}

        <div className="flex justify-around gap-4 mb-8">
          <div>
            <p className="text-center text-lg font-bold uppercase" style={{ color: primaryColor }}>{t('pages.scoringResult.scoreTitle')}</p>
            <p className={`text-center text-2xl font-bold uppercase`}>{`${totalScore} / ${maxScore}`}</p>
            <p className={`text-center text-lg font-bold uppercase`}>{t('labels.points')}</p>
          </div>
        </div>

        <Button
          className="w-full flex items-center justify-center gap-3 uppercase primary-button mb-4"
          onClick={onClick}
        >
          {t('buttons.next')}
        </Button>
      </div>
      <PoweredBy />
    </div>
  )
}

export default ScoringResultPage;
